// The search palette (server docs: search.md; client contract:
// widgets.md "The search palette"). Ctrl/Cmd+K toggles the modal; the
// manifest — the app's declared sources + static entries, already
// permission-filtered and translated server-side — loads once per page
// on first open. Typing matches the STATIC entries locally (label +
// keywords, every token must hit) and fans ONE debounced query out to
// /frm/search/query; results render grouped in declaration order,
// keyboard-navigable (arrows + Enter, the first row is armed). A
// destination builds like everywhere else: `url` verbatim, `route` +
// `params` through g.route.url — and is always a NAVIGATION.

function esc(value) {
	return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function initSearch() {
	const modalEl = document.getElementById('searchModal');
	if (!modalEl || !window.bootstrap) return;
	const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
	document.addEventListener('keydown', (e) => {
		if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
			e.preventDefault();
			modal.toggle();
		}
	});
	const input = document.getElementById('searchModalInput');
	const results = document.getElementById('searchModalResults');
	modalEl.addEventListener('shown.bs.modal', () => {
		if (input) input.focus();
	});
	if (!input || !results || !window.g || !window.g.fetch || !window.g.user) return; // display sites keep the shell only

	let manifest = null;
	let query = '';
	let sourceResults = {};
	let rows = []; // the flat, keyboard-navigable list, in render order
	let active = 0;
	let seq = 0;
	let timer = null;

	async function ensureManifest() {
		if (manifest) return manifest;
		const res = await window.g.fetch('/frm/search/manifest', {}, { progress: false });
		// a FAILED fetch is never cached (race-conditions scan): one network
		// blip at the first Ctrl+K used to leave a permanently empty palette
		// — the next open retries instead
		if (res.success && res.error.code === 0) manifest = res.data;
		return manifest ?? { sources: [], entries: [], minLength: 2 };
	}

	function destination(row) {
		if (row.url) return row.url;
		if (row.route) {
			try {
				return window.g.route.url(row.route, row.params || {});
			} catch (e) {
				return null;
			}
		}
		return null;
	}

	function matches(entry, terms) {
		const hay = (entry.label + ' ' + (entry.keywords || []).join(' ')).toLowerCase();
		return terms.every((t) => hay.includes(t));
	}

	function group(label, groupRows) {
		let html = '<div class="search-modal-label">' + esc(label) + '</div>';
		for (const row of groupRows) {
			const idx = rows.length;
			rows.push(row);
			html += '<button type="button" class="search-result' + (idx === active ? ' is-active' : '') + '" data-index="' + idx + '">'
				+ '<span class="search-result-icon"><i class="' + esc(row.icon || 'ri-arrow-right-line') + '" aria-hidden="true"></i></span>'
				+ '<span class="search-result-text"><span class="search-result-title">' + esc(row.title) + '</span>'
				+ (row.meta ? '<span class="search-result-meta">' + esc(row.meta) + '</span>' : '')
				+ '</span><i class="ri-corner-down-left-line search-result-enter" aria-hidden="true"></i></button>';
		}
		return html;
	}

	function render() {
		if (!manifest) return;
		rows = [];
		const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
		const statics = (terms.length
			? manifest.entries.filter((e) => matches(e, terms))
			: manifest.entries.slice(0, 6)
		).map((e) => ({ title: e.label, icon: e.icon, route: e.route }));
		let html = '';
		if (statics.length) html += group(window.g.translate('Quick links'), statics);
		for (const source of manifest.sources) {
			const found = sourceResults[source.key] || [];
			if (found.length) html += group(source.label, found.map((r) => ({ ...r, icon: r.icon || source.icon })));
		}
		if (!html) {
			html = '<div class="search-modal-empty">'
				+ esc(terms.length ? window.g.translate('No results.') : window.g.translate('Type to search across the app.'))
				+ '</div>';
		}
		results.innerHTML = html;
	}

	function setActive(idx) {
		active = Math.max(0, Math.min(idx, rows.length - 1));
		results.querySelectorAll('.search-result').forEach((el) => {
			el.classList.toggle('is-active', Number(el.getAttribute('data-index')) === active);
		});
		const el = results.querySelector('.search-result.is-active');
		if (el) el.scrollIntoView({ block: 'nearest' });
	}

	function open(row) {
		const dest = destination(row);
		if (dest) window.location.assign(dest);
	}

	async function runQuery() {
		const mine = ++seq;
		const term = query;
		if (term.trim().length < (manifest?.minLength ?? 2)) {
			sourceResults = {};
			render();
			return;
		}
		const res = await window.g.fetch('/frm/search/query', { query: term.trim() }, { progress: false });
		if (mine !== seq) return; // a later keystroke already answered
		// keep the keyboard cursor on the row the user ARROWED to (matched
		// by identity across the re-render) — the late response used to
		// yank it back to row 0 and Enter opened the wrong row
		const prev = rows[active] ?? null;
		sourceResults = (res.success && res.error.code === 0) ? (res.data.results || {}) : {};
		active = 0;
		render();
		if (prev) {
			const again = rows.findIndex((r) => r.title === prev.title && r.route === prev.route && r.url === prev.url);
			if (again > 0) setActive(again);
		}
	}

	modalEl.addEventListener('shown.bs.modal', () => {
		ensureManifest().then(() => {
			active = 0;
			render();
		});
	});
	modalEl.addEventListener('hidden.bs.modal', () => {
		input.value = '';
		query = '';
		sourceResults = {};
		active = 0;
	});

	input.addEventListener('input', () => {
		query = input.value;
		active = 0;
		// the previous query's source results are STALE for the new text —
		// rendering them under it armed the old first row, and a fast Enter
		// navigated to the previous query's record (race-conditions scan);
		// statics still answer instantly, sources return with the response
		sourceResults = {};
		render();
		clearTimeout(timer);
		timer = setTimeout(runQuery, 250);
	});

	input.addEventListener('keydown', (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActive(active + 1);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActive(active - 1);
		} else if (e.key === 'Enter') {
			e.preventDefault();
			if (rows[active]) open(rows[active]);
		}
	});

	results.addEventListener('click', (event) => {
		const btn = event.target.closest('.search-result');
		if (!btn) return;
		const row = rows[Number(btn.getAttribute('data-index'))];
		if (row) open(row);
	});
}
